'use client';

import * as React from 'react';
import useSWR from 'swr';
import { Layout } from '@/components/layout';
import { TokenSavedCounter, LeaderBoard, TopPatterns } from '@/components/stats';
import { apiClient } from '@/lib/api-client';
import { Users, Box, Clock, TrendingUp } from 'lucide-react';

const formatNumber = (num: number) => new Intl.NumberFormat('en-US').format(num);

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  gradient?: string;
  loading?: boolean;
}

function StatCard({ title, value, icon, gradient = 'from-blue-500 to-purple-500', loading }: StatCardProps) {
  if (loading) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 animate-pulse">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-gray-700 rounded-lg" />
          <div className="h-4 bg-gray-700 rounded w-24" />
        </div>
        <div className="h-10 bg-gray-700 rounded w-32" />
      </div>
    );
  }

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:border-gray-600 transition-colors">
      <div className="flex items-center gap-3 mb-3">
        <div className={`p-2 rounded-lg bg-gradient-to-br ${gradient}`}>
          {icon}
        </div>
        <div className="text-sm text-gray-400 uppercase tracking-wide">{title}</div>
      </div>
      <div className={`text-3xl sm:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r ${gradient}`}>
        {value}
      </div>
    </div>
  );
}

function StatsVisualization({ stats, loading }: { stats: { tokensSaved: number; totalPatterns: number; avgLatency: number } | null | undefined; loading: boolean }) {
  if (loading) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 animate-pulse">
        <div className="h-6 bg-gray-700 rounded w-48 mb-4" />
        <div className="space-y-3">
          <div className="h-4 bg-gray-700 rounded w-full" />
          <div className="h-4 bg-gray-700 rounded w-5/6" />
          <div className="h-4 bg-gray-700 rounded w-4/6" />
        </div>
      </div>
    );
  }

  const efficiency = stats?.tokensSaved && stats?.totalPatterns
    ? Math.round(stats.tokensSaved / stats.totalPatterns)
    : 0;

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
      <h2 className="text-xl font-bold text-gray-100 mb-6">Platform Insights</h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-700/30 rounded-lg p-4">
          <div className="text-sm text-gray-400 mb-1">Avg Tokens per Pattern</div>
          <div className="text-2xl font-bold text-green-400">{formatNumber(efficiency)}</div>
        </div>

        <div className="bg-gray-700/30 rounded-lg p-4">
          <div className="text-sm text-gray-400 mb-1">Money Saved</div>
          <div className="text-2xl font-bold text-blue-400">
            ${((stats?.tokensSaved || 0) * 0.00003).toFixed(2)}
          </div>
        </div>

        <div className="bg-gray-700/30 rounded-lg p-4">
          <div className="text-sm text-gray-400 mb-1">Performance Score</div>
          <div className="text-2xl font-bold text-purple-400">
            {(stats?.avgLatency ?? 1000) < 100 ? 'Excellent' : (stats?.avgLatency ?? 1000) < 300 ? 'Good' : 'Fair'}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function StatsPage() {
  const { data: stats, error, isLoading } = useSWR(
    '/stats',
    () => apiClient.getStats(),
    {
      refreshInterval: 30000,
      revalidateOnFocus: true,
      dedupingInterval: 10000,
    }
  );

  const loading = isLoading && !stats;

  return (
    <Layout>
      <div className="min-h-screen bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-100 mb-2">
              Stats Dashboard
            </h1>
            <p className="text-gray-400">
              Real-time analytics and performance metrics
            </p>
          </div>

          {/* Top Stat Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard
              title="Tokens Saved"
              value={loading ? '...' : formatNumber(stats?.tokensSaved || 0)}
              icon={<TrendingUp className="w-6 h-6 text-white" />}
              gradient="from-green-500 to-emerald-500"
              loading={loading}
            />
            <StatCard
              title="Total Patterns"
              value={loading ? '...' : formatNumber(stats?.totalPatterns || 0)}
              icon={<Box className="w-6 h-6 text-white" />}
              gradient="from-blue-500 to-cyan-500"
              loading={loading}
            />
            <StatCard
              title="Total Users"
              value={loading ? '...' : formatNumber(stats?.totalUsers || 0)}
              icon={<Users className="w-6 h-6 text-white" />}
              gradient="from-purple-500 to-pink-500"
              loading={loading}
            />
            <StatCard
              title="Avg Response Time"
              value={loading ? '...' : `${stats?.avgLatency || 0}ms`}
              icon={<Clock className="w-6 h-6 text-white" />}
              gradient="from-orange-500 to-red-500"
              loading={loading}
            />
          </div>

          {/* Middle Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <TokenSavedCounter />
            <StatsVisualization stats={stats} loading={loading} />
          </div>

          {/* Bottom Section - Two Columns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <LeaderBoard />
            <TopPatterns />
          </div>

          {/* Error State */}
          {error && (
            <div className="mt-6 bg-red-900/20 border border-red-700 rounded-lg p-4 text-red-400">
              Failed to load stats. Please try again later.
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
